import { getFootballSeasonYear } from '../../../utils/season';
// src/components/forms/GroupCreationFlow.jsx
import React, { useState, useCallback, useRef } from "react";

// Make logger optional so builds don’t crash if path changes
let _logger = null;
try {
  // eslint-disable-next-line import/no-unresolved
  _logger = require("../../utils/logger").logger;
} catch {
  _logger = {
    component: console.log,
    completion: console.log,
    success: console.log,
    network: console.log,
    database: console.log,
    debug: console.log,
    sports: console.log,
    error: console.error,
  };
}
const logger = _logger;

/* ---------- tiny utils (SSR-safe) ---------- */
const nowServerTS = () =>
  (typeof window !== "undefined" &&
    window.firebase &&
    window.firebase.firestore &&
    window.firebase.firestore.FieldValue &&
    window.firebase.firestore.FieldValue.serverTimestamp)
    ? window.firebase.firestore.FieldValue.serverTimestamp()
    : new Date().toISOString();

const getOrigin = () =>
  (typeof window !== "undefined" && window.location)
    ? window.location.origin
    : "";

const GroupCreationFlow = ({
  createGroup,
  setGameData,
  setCurrentStep,
  gameData = {},
}) => {
  const [groupName, setGroupName] = useState("");
  const [groupDescription, setGroupDescription] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);

  // prevent duplicate/racing operations
  const operationInProgress = useRef(false);
  const lastOperationId = useRef(null);

  const groupCreated = !!gameData?.groupCreated;
  const inviteCode = gameData?.inviteCode || "";

  // ---------- helpers
  const validateGroupInput = (name, description) => {
    const errors = [];
    const n = (name || "").trim();
    if (!n) errors.push("Group name is required");
    else if (n.length < 3) errors.push("Group name must be at least 3 characters");
    else if (n.length > 50) errors.push("Group name must be under 50 characters");
    if ((description || "").length > 200) errors.push("Description must be under 200 characters");
    // Only flag obvious generic names using full-word match (avoid false positives like "testing...")
    const genericWordMatch = /(\btest\b|\bspam\b|\bfake\b)/i;
    if (genericWordMatch.test(n)) errors.push("Please choose a more specific group name");
    return errors;
  };

  const getUserFriendlyError = (err) => {
    const message = (err && err.message) ? err.message : String(err || "Unknown error");
    if (/timeout/i.test(message)) return "Group creation timed out. Please check your connection and try again.";
    if (/network/i.test(message)) return "Network error. Please check your connection and try again.";
    if (/duplicate/i.test(message)) return "A group with this name already exists. Please choose a different name.";
    if (/rate limit/i.test(message)) return "Too many requests. Please wait a moment and try again.";
    return `Failed to create group: ${message}`;
  };

  // ---------- create group
  const handleCreateGroup = useCallback(async () => {
    if (!createGroup || !setGameData) {
      console.error("createGroup/setGameData not provided");
      return;
    }
    if (operationInProgress.current || isCreating) return;

    const validationErrors = validateGroupInput(groupName, groupDescription);
    if (validationErrors.length) {
      setError(validationErrors[0]);
      return;
    }

    const operationId = `create-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    lastOperationId.current = operationId;
    operationInProgress.current = true;
    setIsCreating(true);
    setError(null);

    // clearable timeout to avoid timer leak
    const timeoutRef = { id: null };

    try {
      // IMPORTANT: don't send members here; backend sets members/memberCount
      const payload = {
        name: groupName.trim(),
        description: groupDescription.trim(),
        fanType: gameData?.fanType || "casual",

        // start clean — no team inherited
        team: "",
        teamId: "",
        league: "",
        conference: "",
        division: "",
        teamSelected: false,

        season: getFootballSeasonYear(),

        // Consistent timestamp fields
        createdAt: nowServerTS(),
        lastUpdated: nowServerTS(),
        activityAt: nowServerTS(),

        // maxMembers is fine to include as a hint if your backend accepts it
        maxMembers: 10,
      };

      logger.component("🆕 Creating group with clean, isolated state", payload);

      const timeout = new Promise((_, reject) => {
        timeoutRef.id = setTimeout(() => reject(new Error("Group creation timed out")), 30_000);
      });

      const result = await Promise.race([createGroup(payload), timeout]);

      if (!result || !result.inviteCode || !result.groupId) {
        throw new Error("Invalid response from group creation service");
      }
      if (lastOperationId.current !== operationId) {
        logger.debug("Result from superseded operation ignored");
        return;
      }

      logger.success("✅ Group created", result);

      const isoNow = new Date().toISOString();
      const stateUpdate = {
        groupId: result.groupId,
        groupName: payload.name,
        groupDescription: payload.description,
        inviteCode: result.inviteCode,

        isGroupCreator: true,
        groupCreated: true,
        groupMode: "create",

        fanType: gameData?.fanType || "casual",
        team: "",
        teamId: "",
        league: "",
        conference: "",
        division: "",
        teamSelected: false,

        positionGrades: {},
        coachGrades: {},
        predictions: { gameResults: {}, topPlayer: {}, conferenceCoachRankings: {} },
        status: "draft",
        isSubmitted: false,
        isCompleted: false,
        submittedAt: null,

        continuingProgress: false,
        _continuingExisting: false,
        _existingDocId: null,

        createdAt: isoNow,
        lastUpdated: isoNow,
        activityAt: isoNow,
      };

      setGameData(prev => {
        if (prev?.groupCreated && prev?.groupId && prev.groupId !== result.groupId) {
          logger.debug("Race detected, preserving newer group");
          return prev;
        }
        logger.component("🔄 Setting clean group state (no inheritance)");
        return { ...prev, ...stateUpdate };
      });
    } catch (err) {
      console.error("❌ Group creation failed:", err);
      // Rollback minimal flags
      setGameData(prev => ({
        ...prev,
        groupId: null,
        groupCreated: false,
        inviteCode: "",
        isGroupCreator: false,
        groupMode: null,
      }));
      setError(getUserFriendlyError(err));
    } finally {
      if (timeoutRef.id) clearTimeout(timeoutRef.id);
      if (lastOperationId.current === operationId) {
        operationInProgress.current = false;
        setIsCreating(false);
      }
    }
  }, [createGroup, setGameData, isCreating, groupName, groupDescription, gameData?.fanType]);

  // ---------- navigation
  const handleBackClick = useCallback(() => {
    logger.component("🔄 Back to dashboard from group creation");
    if (!setGameData || !setCurrentStep) return;

    setGameData(prev => ({
      groupMode: null,
      groupId: null,
      groupName: "",
      groupDescription: "",
      inviteCode: "",
      isGroupCreator: false,
      groupCreated: false,
      groupMembers: [],
      groupInfo: null,

      team: null,
      teamId: null,
      league: null,
      conference: null,
      division: null,
      season: getFootballSeasonYear(),
      teamSelected: false,

      fanType: null,
      formActivated: false,
      participantName: "",
      avatar: null,

      positionGrades: {},
      coachGrades: {},
      predictions: {},
      completionPercentage: 0,
      submittedAt: null,
      submittedData: null,
      status: null,

      isJoiningPremiumGroup: false,
      continuingProgress: false,
      _continuingExisting: false,
      _existingDocId: null,

      userId: prev?.userId,
      createdAt: prev?.createdAt,
      lastReset: new Date().toISOString(),
    }));

    setGroupName("");
    setGroupDescription("");
    setError(null);
    setIsCreating(false);
    operationInProgress.current = false;

    setCurrentStep("dashboard");
    if (typeof window !== "undefined") {
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
    }
  }, [setGameData, setCurrentStep]);

  const handleContinueToTeamSelection = useCallback(() => {
    if (!setCurrentStep) return;
    if (!gameData?.groupCreated || !gameData?.groupId || !gameData?.inviteCode) {
      setError("Group setup incomplete. Please try creating the group again.");
      return;
    }
    logger.sports("🏈 Proceeding to team selection", {
      groupId: gameData.groupId,
      inviteCode: gameData.inviteCode,
    });
    setCurrentStep("team");
    if (typeof window !== "undefined") {
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
    }
  }, [gameData, setCurrentStep]);

  // ---------- sharing
  const copySmartInvite = useCallback(async () => {
    try {
      if (!inviteCode) throw new Error("No invite code available");
      const smartUrl = `${getOrigin()}/?invite=${inviteCode}`;
      const shareMessage = `🏈 Join me on First Snap! Click here: ${smartUrl}

No group code needed — this link takes you directly to my group!`;

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareMessage);
      } else {
        // fallback for non-secure origins / older browsers
        const ta = document.createElement("textarea");
        ta.value = shareMessage;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }

      logger.success("📋 Smart invite copied:", shareMessage);
      alert("🎉 Smart invite link copied!\n\nThis link automatically takes them to your group.");
    } catch (err) {
      console.error("❌ Copy failed:", err);
    }
  }, [inviteCode]);

  const sendSmartInviteEmail = useCallback(() => {
    if (!inviteCode || !gameData?.groupName) {
      alert("Group information not available");
      return;
    }
    const smartUrl = `${getOrigin()}/?invite=${inviteCode}`;
    const subject = `Join my First Snap group: ${gameData.groupName}!`;
    const body = `Hey!

I've created a First Snap group and I'd love for you to join!

Group Name: ${gameData.groupName}
${gameData.groupDescription ? `Description: ${gameData.groupDescription}\n` : ""}

🏈 Just click this link to join automatically: ${smartUrl}

Let's see who has the best pulse on the team this season!

- ${gameData.participantName || "Your friend"}`;
    const mailto = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    if (typeof window !== "undefined") window.open(mailto);
  }, [inviteCode, gameData]);

  const shareSmartInvite = useCallback(async () => {
    if (!inviteCode) return;
    const smartUrl = `${getOrigin()}/?invite=${inviteCode}`;
    const shareData = {
      title: `Join my football group: ${gameData.groupName || "Football Group"}`,
      text: "🏈 Join my football analysis group! No code needed — this link takes you directly to the group.",
      url: smartUrl,
    };
    try {
      if (navigator.share && navigator.canShare?.(shareData)) {
        await navigator.share(shareData);
        logger.component("📱 Shared via native share API");
      } else {
        await copySmartInvite();
      }
    } catch (err) {
      console.error("❌ Share failed:", err);
      await copySmartInvite();
    }
  }, [inviteCode, gameData.groupName, copySmartInvite]);

  // ---------- success UI
  if (groupCreated && inviteCode) {
    return (
      <div style={{ maxWidth: 500, margin: "0 auto", fontFamily: "Inter, sans-serif", padding: 20 }}>
        <div style={{ backgroundColor: "#f0f9ff", border: "2px solid #0ea5e9", borderRadius: 12, padding: 32, textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: "#0f172a", marginBottom: 12 }}>Group Created Successfully!</h2>
          <p style={{ fontSize: 16, color: "#64748b", marginBottom: 24 }}>
            Your group "{gameData.groupName}" is ready. Share with friends and family below!
          </p>

          <div style={{ backgroundColor: "white", border: "2px dashed #0ea5e9", borderRadius: 8, padding: 20, marginBottom: 20 }}>
            <p style={{ fontSize: 12, color: "#64748b", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Invite Code
            </p>
            <div style={{ fontSize: 32, fontWeight: 700, color: "#0f172a", fontFamily: "monospace", letterSpacing: "0.1em", marginBottom: 16 }}>
              {inviteCode}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center" }}>
              <button onClick={copySmartInvite} style={{ backgroundColor: "#0ea5e9", color: "white", border: "none", borderRadius: 6, padding: "8px 16px", fontSize: 14, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                🔗 Copy Smart Link
              </button>
              <button onClick={sendSmartInviteEmail} style={{ backgroundColor: "#10b981", color: "white", border: "none", borderRadius: 6, padding: "8px 16px", fontSize: 14, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                📧 Email Invite
              </button>
              <button onClick={shareSmartInvite} style={{ backgroundColor: "#2563eb", color: "white", border: "none", borderRadius: 6, padding: "8px 16px", fontSize: 14, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                📱 Share
              </button>
            </div>
          </div>

          <div style={{ backgroundColor: "#fefce8", border: "1px solid #eab308", borderRadius: 8, padding: 16, marginBottom: 24, textAlign: "left" }}>
            <h4 style={{ fontSize: 14, fontWeight: 600, color: "#92400e", marginBottom: 8, textAlign: "center" }}>📱 Message Preview:</h4>
            <div style={{ fontSize: 14, color: "#92400e", fontStyle: "italic", textAlign: "center", backgroundColor: "white", padding: 12, borderRadius: 6, border: "1px solid #fde047" }}>
              {"Join me on First Snap! Click here: "}{`${getOrigin()}/?invite=${inviteCode}`}
            </div>
            <p style={{ fontSize: 12, color: "#059669", marginTop: 8, textAlign: "center" }}>
              🎯 <strong>No group code needed!</strong> Link takes them directly to your group.
            </p>
          </div>

          <div style={{ backgroundColor: "#fefce8", border: "1px solid #eab308", borderRadius: 8, padding: 16, marginBottom: 24, textAlign: "left" }}>
            <h4 style={{ fontSize: 14, fontWeight: 600, color: "#92400e", marginBottom: 8 }}>What's Next:</h4>
            <div style={{ fontSize: 14, color: "#92400e", lineHeight: 1.5 }}>
              1. Choose your team and set up your profile<br />
              2. Share the invite code with friends<br />
              3. Everyone completes their analysis and competes!
            </div>
          </div>

          <button
            onClick={handleContinueToTeamSelection}
            style={{ backgroundColor: "white", color: "black", border: "1px solid #e5e7eb", borderRadius: 8, padding: "16px 32px", fontSize: 16, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f3f4f6")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "white")}
          >
            Continue Setup →
          </button>
          <p style={{ fontSize: 12, color: "#6b7280", marginTop: 12 }}>
            💡 You can invite friends now or continue setting up your group
          </p>
        </div>

        <style>{`
          @keyframes spin { 0% { transform: rotate(0deg) } 100% { transform: rotate(360deg) } }
        `}</style>
      </div>
    );
  }

  // ---------- creation form UI
  return (
    <div style={{ maxWidth: 500, margin: "0 auto", fontFamily: "Inter, sans-serif", padding: 20 }}>
      <div
        className="sbe-team-selection-container"
        style={{
          maxWidth: 480,
          margin: "0 auto",
          background: "white",
          borderRadius: 12,
          boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
          padding: "32px 24px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24, textAlign: "left" }}>
          <div style={{ fontSize: 32, flexShrink: 0 }}>👥</div>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontSize: 24, fontWeight: 600, color: "#257980", margin: "0 0 4px 0" }}>Create Your Group</h2>
            <p style={{ fontSize: 16, color: "#6b7280", margin: 0 }}>
              Set up a group to compete with friends and family!
            </p>
          </div>
        </div>

        {error && (
          <div style={{ backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "12px 16px", marginBottom: 16, textAlign: "center", fontSize: 14 }}>
            <span style={{ color: "#dc2626" }}>
              <strong>Error:</strong> {error}
            </span>
          </div>
        )}

        <div className="sbe-form-container" style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          <div className="sbe-form-section">
            <label style={{ display: "block", fontSize: 16, fontWeight: 600, color: "#374151", marginBottom: 8 }}>
              Group Name <span style={{ color: "#dc2626" }}>*</span>
            </label>
            <input
              type="text"
              placeholder="e.g., Smith Family Football Analysis"
              value={groupName}
              onChange={(e) => { setGroupName(e.target.value); if (error) setError(null); }}
              disabled={isCreating}
              maxLength={50}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: error ? "2px solid #dc2626" : "2px solid #d1d5db",
                borderRadius: 8,
                fontSize: 16,
                backgroundColor: isCreating ? "#f9fafb" : "white",
                color: isCreating ? "#9ca3af" : "#1f2937",
                cursor: isCreating ? "not-allowed" : "text",
                transition: "all 0.2s",
                outline: "none",
              }}
              onFocus={(e) => !e.currentTarget.disabled && (e.currentTarget.style.borderColor = "#257980")}
              onBlur={(e) => !e.currentTarget.disabled && (e.currentTarget.style.borderColor = error ? "#dc2626" : "#d1d5db")}
            />
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>{groupName.length}/50 characters</div>
          </div>

          <div className="sbe-form-section">
            <label style={{ display: "block", fontSize: 16, fontWeight: 600, color: "#374151", marginBottom: 8 }}>
              Description (Optional)
            </label>
            <textarea
              placeholder="Add a description for your group..."
              value={groupDescription}
              onChange={(e) => setGroupDescription(e.target.value)}
              disabled={isCreating}
              maxLength={200}
              rows={3}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "2px solid #d1d5db",
                borderRadius: 8,
                fontSize: 16,
                backgroundColor: isCreating ? "#f9fafb" : "white",
                color: isCreating ? "#9ca3af" : "#1f2937",
                cursor: isCreating ? "not-allowed" : "text",
                transition: "all 0.2s",
                outline: "none",
                resize: "vertical",
                fontFamily: "inherit",
              }}
              onFocus={(e) => !e.currentTarget.disabled && (e.currentTarget.style.borderColor = "#257980")}
              onBlur={(e) => !e.currentTarget.disabled && (e.currentTarget.style.borderColor = "#d1d5db")}
            />
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>{groupDescription.length}/200 characters</div>
          </div>

          <div style={{ backgroundColor: "#f0f9ff", padding: 20, borderRadius: 12, border: "2px solid #257980", marginBottom: 8 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#257980", marginBottom: 12, textTransform: "uppercase", letterSpacing: "0.5px" }}>
              ℹ️ How It Works
            </div>
            <div style={{ fontSize: 14, color: "#64748b", lineHeight: 1.6 }}>
              <div style={{ marginBottom: 4 }}><strong>1.</strong> Create your group and get an invite code</div>
              <div style={{ marginBottom: 4 }}><strong>2.</strong> Choose your team and set up your profile</div>
              <div style={{ marginBottom: 4 }}><strong>3.</strong> Share the invite code with friends</div>
              <div><strong>4.</strong> Everyone completes their analysis and competes!</div>
            </div>
          </div>

          <div className="sbe-navigation-buttons" style={{ display: "flex", gap: 16, marginTop: 32 }}>
            <button
              className="sbe-button-secondary"
              style={{
                flex: 1,
                padding: "12px 24px",
                backgroundColor: "white",
                border: "2px solid #e5e7eb",
                borderRadius: 8,
                fontSize: 16,
                fontWeight: 600,
                color: "#374151",
                cursor: isCreating ? "not-allowed" : "pointer",
                transition: "all 0.2s",
                opacity: isCreating ? 0.5 : 1,
              }}
              onClick={handleBackClick}
              disabled={isCreating}
              onMouseEnter={(e) => {
                if (!isCreating) {
                  e.currentTarget.style.backgroundColor = "#f9fafb";
                  e.currentTarget.style.borderColor = "#9ca3af";
                }
              }}
              onMouseLeave={(e) => {
                if (!isCreating) {
                  e.currentTarget.style.backgroundColor = "white";
                  e.currentTarget.style.borderColor = "#e5e7eb";
                }
              }}
            >
              ← Back
            </button>

            <button
              className="sbe-button-primary"
              style={{
                flex: 2,
                padding: "12px 24px",
                fontSize: 16,
                backgroundColor: !groupName.trim() || isCreating ? "#9ca3af" : "#257980",
                color: !groupName.trim() || isCreating ? "#6b7280" : "white",
                border: "none",
                borderRadius: 8,
                cursor: !groupName.trim() || isCreating ? "not-allowed" : "pointer",
                fontWeight: 600,
                transition: "all 0.2s",
                outline: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
              onClick={handleCreateGroup}
              disabled={!groupName.trim() || isCreating || operationInProgress.current}
              onMouseEnter={(e) => {
                if (groupName.trim() && !isCreating) e.currentTarget.style.backgroundColor = "#1e6165";
              }}
              onMouseLeave={(e) => {
                if (groupName.trim() && !isCreating) e.currentTarget.style.backgroundColor = "#257980";
              }}
            >
              {isCreating ? (
                <>
                  <div style={{ width: 16, height: 16, border: "2px solid currentColor", borderTop: "2px solid transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
                  Creating...
                </>
              ) : (
                "Create Group"
              )}
            </button>
          </div>
        </div>

        <style>{`
          @keyframes spin { 0% { transform: rotate(0deg) } 100% { transform: rotate(360deg) } }
        `}</style>
      </div>
    </div>
  );
};

export default GroupCreationFlow;
